<template>
  <div class="page">
    <div>
      <h5 class="mb-2" style="color: #0055A6; font-weight: bolder;">{{ $t('removableMedia') }}</h5>
    </div>
    <div class="div-wrapper p-5">
      <div class="form-grid">
        <div class="form-row">
          <label class="form-label">{{ $t('name') }}</label>
          <InputText v-model="form.name" class="compact-input" :placeholder="$t('name')" />
        </div>

        <div class="form-row">
          <label class="form-label">{{ $t('removableMediaAccess') }}</label>
          <Dropdown v-model="form.eRemovableMediaAccess" :options="accessOptions" optionLabel="label"
            optionValue="value" class="compact-dropdown" :placeholder="$t('select')" appendTo="self" showClear />
        </div>
      </div>

      <hr style="margin-top: 2rem; margin-bottom: 1rem;" />

      <div class="d-flex justify-content-end gap-3">
        <Button class="cancel-btn" @click="onCancel">{{ $t('cancel') }}</Button>
        <Button class="save-btn" :disabled="!isValid" @click="onSave">{{ $t('save') }}</Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import Button from 'primevue/button'
import { GeneralActionTypes } from '@/store/modules/general/action-types'
import { useStore } from '@/store'
import Swal from 'sweetalert2'
import { useRoute } from 'vue-router'

const emit = defineEmits(['save', 'cancel'])

const store = useStore()
const { t } = useI18n()

const form = ref({
  name: '',
  eRemovableMediaAccess: ''
})
const removableMedia = ref({})

const accessOptions = computed(() => store.state.general.enums['RemovableMediaAccess'] || [])

const isValid = computed(() =>
  String(form.value.name || '').trim().length > 0 &&
  form.value.eRemovableMediaAccess !== '' &&
  form.value.eRemovableMediaAccess !== null &&
  form.value.eRemovableMediaAccess !== undefined
)
const group = localStorage.getItem("agms_group");
const groupId = group ? JSON.parse(group)?.mainid : null;

const templateId = ref(null)

const isTemplate = ref(false)

async function getRemovableMedia() {
  try {
    let resp = await store.dispatch(GeneralActionTypes.GET_REMOVABLE_MEDIA, groupId);
    const payload = resp?.entityData ?? resp?.data ?? resp ?? {};
    removableMedia.value = payload;
    form.value.name = payload?.name ?? '';
    form.value.eRemovableMediaAccess = payload?.eRemovableMediaAccess ?? '';
    
    if (resp.isSuccess) {
      if (!removableMedia.value) {
        await getRemovableMediaInheritanceInfo(null, groupId, null);
      } else {
        await getRemovableMediaInheritanceInfo(removableMedia.value.id, groupId, null);
      }
    } else {
      await getRemovableMediaInheritanceInfo(null, groupId, null);
    }
  } catch (e) {
    console.warn('Failed to load removable media', e);
  }
}

async function getRemovableMediaTemplate() {
  try {
    let resp = await store.dispatch(GeneralActionTypes.GET_REMOVABLE_MEDIA_TEMPLATE, templateId.value);
    const payload = resp?.entityData ?? resp?.data ?? resp ?? {};
    removableMedia.value = payload.templatePolicyViewModel.removableMedia;
    form.value.name = removableMedia.value?.name ?? '';
    form.value.eRemovableMediaAccess = removableMedia.value?.eRemovableMediaAccess ?? '';
    if (resp.isSuccess) {
      if (!removableMedia.value) {
        await getRemovableMediaInheritanceInfo(null, null, templateId.value);
      } else {
        await getRemovableMediaInheritanceInfo(removableMedia.value.id, null, templateId.value);
      }
    } else {
      await getRemovableMediaInheritanceInfo(null, null, templateId.value);
    }
  } catch (e) {
    console.warn('Failed to load removable media template', e);
  }
}

async function saveRemovableMedia() {
  let payload = {
    id: removableMedia.value.id ? removableMedia.value.id : 0,
    name: form.value.name,
    eRemovableMediaAccess: form.value.eRemovableMediaAccess,
    eLocation: 0

  }
  try {
    let resp = null;
    if (isTemplate.value) {
      payload.templateId = templateId.value;
      resp = await store.dispatch(GeneralActionTypes.CREATE_OR_UPDATE_REMOVABLE_MEDIA_TEMPLATE, payload);
    } else {
      payload.groupId = groupId;
      resp = await store.dispatch(GeneralActionTypes.CREATE_OR_UPDATE_REMOVABLE_MEDIA, payload);
    }
    if (resp.isSuccess) {
      Swal.fire({
        icon: 'success',
        title: t('success'),
        text: t('removableMediaSuccess'),
      })
      if (isTemplate.value) {
        getRemovableMediaTemplate();
      } else {
        getRemovableMedia();
      }
    }
  } catch (e) {
    const message = e.response?.data?.message || e.message || t('removableMediaFailed');
    Swal.fire({
      icon: 'error',
      title: t('error'),
      text: message,
    })
  }
}

function onCancel() {
  form.value = {
    name: '',
    eRemovableMediaAccess: ''
  }
}

function onSave() {
  if (!isValid.value) return
  saveRemovableMedia()
}

const fetchEnumAndStore = async (name) => {
  await store.dispatch(GeneralActionTypes.GET_ENUM_BY_NAME, name);
};
async function getRemovableMediaInheritanceInfo(removableMediaId, groupId, templateId) {
  await store.dispatch(GeneralActionTypes.GET_REMOVABLE_MEDIA_INHERITANCE_INFO, {
    removableMediaId,
    groupId,
    templateId
  });
}

onMounted(() => {
  fetchEnumAndStore('RemovableMediaAccess')

  const routeName = useRoute().name;
  if (routeName == 'templateRemovableMedia') {
    isTemplate.value = true;
    const templateStr = localStorage.getItem("agms_template");
    const mainId = templateStr ? JSON.parse(templateStr)?.mainid : null;
    templateId.value = mainId;
    getRemovableMediaTemplate();
    getRemovableMediaInheritanceInfo(null, null, templateId.value);
  } else {
    isTemplate.value = false;
    getRemovableMedia();
    getRemovableMediaInheritanceInfo(null, groupId, null);
  }
})
</script>

<style scoped>
.div-wrapper {
  margin-top: 20px;
  width: 720px;
  background: #fff;
  box-shadow: 0px 2px 14px 0px #00000014;

}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 0.85fr;
  align-items: center;
  gap: 16px;
}

.form-label {
  color: #0055A6;
  font-weight: 600;
  font-size: 14px;
}

:deep(.p-dropdown .p-dropdown-clear-icon) {
  width: 0.7rem;
  margin-right: -0.5rem;
}

.save-btn {
  background-color: #003060;
  color: #fff;
  border: none;
  border-radius: 4px;
  height: 36px;
  width: 150px;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 500;
}

.cancel-btn {
  background-color: #F7FBFF;
  border: 1px solid #D2D9E2;
  color: #01172FB2;
  border-radius: 4px;
  height: 36px;
  width: 150px;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 600;
  
}

.compact-input:deep(.p-inputtext),
:deep(.compact-input.p-inputtext) {
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
  height: 32px;
}

:deep(.compact-dropdown.p-dropdown) {
  height: 32px;
}

:deep(.compact-dropdown .p-dropdown-label) {
  padding: 0.5rem 0.5rem;
  font-size: 0.85rem;
  line-height: 1.2;
}

:deep(.compact-dropdown .p-dropdown-trigger) {
  width: 2rem;
}

.page {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
</style>